// Spec 026 (S4.2) client module for chunked multipart staging batches. A plain sibling
// module, not a Stimulus controller -- api_dashboard_controller.ts (S4.3) owns folder-pick
// filtering/normalization and wires this module's callbacks into the DOM.
//
// NOTE for S4.1/S4.3 (Gabriel to reconcile): this module does not assume any Stimulus
// target/value names. It expects a plain `StagedUploadBatchesConfig` object built by the
// controller from its own data values (batch/session caps, BATCH_CONCURRENCY, and the four
// staging endpoint paths -- FR-033). Endpoint path fields are named `openPath`, `batchesPath`,
// `filesPath`, `sessionPath` below; pick matching `data-*-value` names in S4.1's markup.

export type BatchStatus = 'queued' | 'uploading' | 'staged' | 'failed';
export type BatchFailureReason = 'transport' | 'server' | 'validation' | null;

export interface StagedUploadBatchSnapshot {
  id: string;
  names: string[];
  status: BatchStatus;
  progressPercent: number;
  message: string | null;
  failureReason: BatchFailureReason;
  offerRemove: boolean;
  canRetry: boolean;
}

interface FieldCentricError {
  rows: number[];
  field: string;
  message: string;
  code?: string;
}

interface XhrOutcome {
  status: number;
  json: { errors?: FieldCentricError[]; [key: string]: any } | null;
  networkError: boolean;
}

export interface StagedUploadBatchesConfig {
  openPath: string;
  batchesPath: string;
  filesPath: string;
  sessionPath: string;
  batchMaxFiles: number;
  batchMaxBytes: number;
  batchConcurrency: number;
  onBatchesChanged: (batches: StagedUploadBatchSnapshot[]) => void;
  onSessionInvalid: (message: string) => void;
  onTotalsChanged?: (totals: { count: number; bytes: number }) => void;
  onOpenFailed?: (message: string) => void;
}

// FR-008: automatic retries apply only to transport failures (network/5xx/non-JSON, A4);
// an Inspect/session-cap 422 is never auto-retried since the same bytes will fail again.
const AUTO_RETRY_LIMIT = 2;

// Mirrors api_dashboard_controller.ts's ASCII_TRIM/normalizeStagedName (never String#trim,
// which also strips NBSP/BOM) so remove-by-name matches names computed on the picker side.
const ASCII_TRIM = /^[ \t\r\n]+|[ \t\r\n]+$/g;

function normalizeName(name: string): string {
  return name.replace(ASCII_TRIM, '').normalize('NFC');
}

// Greedy first-fit bin packing (FR-006): each file goes into the first open batch with room
// left for it, else a new batch opens. Callers must pre-filter files against the per-file
// byte cap -- a single file over `maxBytes` is placed alone rather than dropped.
export function packIntoBatches(files: File[], maxFiles: number, maxBytes: number): File[][] {
  const bins: { files: File[]; bytes: number }[] = [];

  files.forEach((file) => {
    const bin = bins.find((candidate) => candidate.files.length < maxFiles && candidate.bytes + file.size <= maxBytes);
    if (bin) {
      bin.files.push(file);
      bin.bytes += file.size;
    } else {
      bins.push({ files: [file], bytes: file.size });
    }
  });

  return bins.map((bin) => bin.files);
}

class Batch {
  readonly id: string;
  files: File[];
  status: BatchStatus = 'queued';
  progressPercent = 0;
  message: string | null = null;
  failureReason: BatchFailureReason = null;
  offerRemove = false;
  autoRetriesUsed = 0;
  xhr: XMLHttpRequest | null = null;

  constructor(id: string, files: File[]) {
    this.id = id;
    this.files = files;
  }

  get names(): string[] {
    return this.files.map((file) => normalizeName(file.name));
  }

  snapshot(): StagedUploadBatchSnapshot {
    return {
      id: this.id,
      names: this.names,
      status: this.status,
      progressPercent: this.progressPercent,
      message: this.message,
      failureReason: this.failureReason,
      offerRemove: this.offerRemove,
      canRetry: this.status === 'failed',
    };
  }
}

type Classification = 'success' | 'validation' | 'session_invalid' | 'transport' | 'server';

const TRANSPORT_FAILURE_MESSAGE = 'Upload failed. Check your connection and retry this batch.';
const SERVER_FAILURE_MESSAGE = 'The server could not accept this batch. Nothing from it was saved -- retry this batch.';

// Manages one staging session end to end: open, greedy-packed batch POSTs with bounded
// concurrency and progress, per-batch retry/remove, and cancel. See spec 026 US-001/US-002.
export class StagedUploadBatches {
  private config: StagedUploadBatchesConfig;
  private batches: Batch[] = [];
  private queue: Batch[] = [];
  private stagingToken: string | null = null;
  private nextBatchId = 0;
  private inFlight = 0;
  private cancelled = false;

  constructor(config: StagedUploadBatchesConfig) {
    this.config = config;
  }

  currentToken(): string | null {
    return this.stagingToken;
  }

  allStaged(): boolean {
    return this.batches.length > 0 && this.batches.every((batch) => batch.status === 'staged');
  }

  // Names of every file whose batch is not yet staged -- feeds FR-010's submit-blocking message.
  pendingNames(): string[] {
    return this.batches.reduce<string[]>((names, batch) => (batch.status === 'staged' ? names : names.concat(batch.names)), []);
  }

  // FR-006: packs a freshly picked (already filtered/normalized) file set into batches and
  // starts uploading; opens the session first if this is the first call.
  start(files: File[]): void {
    if (files.length === 0) return;
    this.cancelled = false;

    const groups = packIntoBatches(files, this.config.batchMaxFiles, this.config.batchMaxBytes);
    const newBatches = groups.map((group) => new Batch(String(this.nextBatchId++), group));
    this.batches = this.batches.concat(newBatches);
    this.emit();

    if (this.stagingToken) {
      this.enqueue(newBatches);
    } else {
      this.openSession(newBatches);
    }
  }

  // FR-008: a failed batch is retryable alone -- never re-posts any other batch.
  retryBatch(id: string): void {
    const batch = this.batches.find((candidate) => candidate.id === id);
    if (!batch || batch.status !== 'failed') return;

    batch.autoRetriesUsed = 0;
    batch.message = null;
    batch.offerRemove = false;
    batch.failureReason = null;
    this.enqueue([batch]);
  }

  // FR-009: per-file remove. Names still only client-side (queued/failed) are dropped locally;
  // names already staged are purged server-side via DELETE .../staging/files.
  removeFiles(names: string[]): void {
    const wanted = new Set(names.map(normalizeName));
    const stagedNamesToDelete: string[] = [];

    this.batches = this.batches.filter((batch) => {
      if (batch.status === 'uploading') return true; // an in-flight batch can't be safely mutated

      const before = batch.files.length;
      batch.files = batch.files.filter((file) => {
        if (!wanted.has(normalizeName(file.name))) return true;
        if (batch.status === 'staged') stagedNamesToDelete.push(normalizeName(file.name));
        return false;
      });

      if (batch.files.length === before) return true;
      if (batch.files.length === 0) return false; // drop the now-empty batch

      // Remaining files in a fixed-up failed batch get a fresh attempt automatically.
      if (batch.status === 'failed') {
        batch.status = 'queued';
        batch.message = null;
        batch.offerRemove = false;
        batch.failureReason = null;
        this.queue.push(batch);
      }
      return true;
    });

    this.pump();
    this.emit();

    if (stagedNamesToDelete.length > 0 && this.stagingToken) this.deleteFiles(stagedNamesToDelete);
  }

  // FR-009: abort in-flight batches and DELETE the session; local state clears immediately
  // rather than waiting on the (best-effort) server response.
  cancel(): void {
    this.cancelled = true;
    const token = this.stagingToken;

    this.batches.forEach((batch) => batch.xhr?.abort());
    this.batches = [];
    this.queue = [];
    this.inFlight = 0;
    this.stagingToken = null;
    this.emit();
    this.config.onTotalsChanged?.({ count: 0, bytes: 0 });

    if (token) {
      this.sendXhr('DELETE', this.config.sessionPath, JSON.stringify({ staging_token: token }), true, null, () => {
        // Best-effort: client state is already cleared regardless of the server's response.
      });
    }
  }

  private openSession(pendingBatches: Batch[]): void {
    this.sendXhr('POST', this.config.openPath, '{}', true, null, (outcome) => {
      if (outcome.status === 201 && typeof outcome.json?.staging_token === 'string') {
        this.stagingToken = outcome.json.staging_token;
        this.enqueue(pendingBatches);
        return;
      }

      this.batches = this.batches.filter((batch) => pendingBatches.indexOf(batch) === -1);
      this.emit();

      const classification = this.classify(outcome);
      if (classification === 'session_invalid') {
        this.config.onSessionInvalid(this.firstErrorMessage(outcome) ?? 'upload session is invalid or has expired');
      } else if (classification === 'server') {
        this.config.onOpenFailed?.('The server could not open an upload session. Try again in a moment.');
      } else {
        this.config.onOpenFailed?.('Could not open an upload session. Check your connection and try again.');
      }
    });
  }

  private enqueue(batches: Batch[]): void {
    batches.forEach((batch) => {
      batch.status = 'queued';
      this.queue.push(batch);
    });
    this.pump();
  }

  // AD-5: caps in-flight requests at BATCH_CONCURRENCY; called after every settle so the
  // next queued batch starts immediately.
  private pump(): void {
    while (this.inFlight < this.config.batchConcurrency && this.queue.length > 0) {
      const batch = this.queue.shift();
      if (batch) this.uploadBatch(batch);
    }
  }

  private uploadBatch(batch: Batch): void {
    if (this.cancelled || !this.stagingToken) return;

    this.inFlight += 1;
    batch.status = 'uploading';
    batch.progressPercent = 0;
    batch.message = null;
    batch.offerRemove = false;
    this.emit();

    const formData = new FormData();
    formData.append('staging_token', this.stagingToken);
    batch.files.forEach((file) => formData.append('staged_files[]', file, file.name));

    batch.xhr = this.sendXhr(
      'POST',
      this.config.batchesPath,
      formData,
      false,
      (percent) => {
        batch.progressPercent = percent;
        this.emit();
      },
      (outcome) => {
        this.inFlight -= 1;
        batch.xhr = null;
        this.handleBatchOutcome(batch, outcome);
        this.pump();
      }
    );
  }

  private handleBatchOutcome(batch: Batch, outcome: XhrOutcome): void {
    if (this.cancelled) return;

    const classification = this.classify(outcome);

    if (classification === 'success') {
      batch.status = 'staged';
      batch.progressPercent = 100;
      batch.message = null;
      if (outcome.json?.totals) this.config.onTotalsChanged?.(outcome.json.totals);
      this.emit();
      return;
    }

    if (classification === 'session_invalid') {
      this.stagingToken = null;
      this.batches.forEach((candidate) => candidate.xhr?.abort());
      this.queue = [];
      this.config.onSessionInvalid(this.firstErrorMessage(outcome) ?? 'upload session is invalid or has expired');
      return;
    }

    if (classification === 'validation') {
      batch.status = 'failed';
      batch.failureReason = 'validation';
      batch.offerRemove = true;
      batch.message = `${this.firstErrorMessage(outcome) ?? 'These files were not accepted.'} Retrying the same bytes will fail again -- remove or replace them first.`;
      this.emit();
      return;
    }

    // Transport failure (network error or non-JSON body -- Hiram A4) or a 5xx. Both are worth
    // auto-retrying; only the exhausted-retry copy distinguishes them.
    batch.failureReason = classification === 'server' ? 'server' : 'transport';
    if (batch.autoRetriesUsed < AUTO_RETRY_LIMIT) {
      batch.autoRetriesUsed += 1;
      this.queue.push(batch);
      return;
    }

    batch.status = 'failed';
    batch.offerRemove = false;
    batch.message = classification === 'server' ? SERVER_FAILURE_MESSAGE : TRANSPORT_FAILURE_MESSAGE;
    this.emit();
  }

  private deleteFiles(names: string[]): void {
    const body = JSON.stringify({ staging_token: this.stagingToken, names });
    this.sendXhr('DELETE', this.config.filesPath, body, true, null, (outcome) => {
      if (this.classify(outcome) === 'session_invalid') {
        this.stagingToken = null;
        this.config.onSessionInvalid(this.firstErrorMessage(outcome) ?? 'upload session is invalid or has expired');
        return;
      }
      if (outcome.json?.totals) this.config.onTotalsChanged?.(outcome.json.totals);
    });
  }

  private classify(outcome: XhrOutcome): Classification {
    if (outcome.networkError) return 'transport';
    // Must precede A4's non-JSON rule: a Rails 500 renders an HTML error page, so testing
    // `json === null` first would report every server-side crash as a connection failure.
    if (outcome.status >= 500) return 'server';
    if (outcome.json === null) return 'transport'; // A4: non-JSON body counts as transport failure
    if (outcome.status === 200 || outcome.status === 201) return 'success';
    if (outcome.status === 422) {
      const code = outcome.json.errors?.[0]?.code;
      return code === 'staging_session_invalid' || code === 'already_submitted' ? 'session_invalid' : 'validation';
    }
    return 'transport';
  }

  private firstErrorMessage(outcome: XhrOutcome): string | null {
    return outcome.json?.errors?.[0]?.message ?? null;
  }

  private emit(): void {
    this.config.onBatchesChanged(this.batches.map((batch) => batch.snapshot()));
  }

  private csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? '';
  }

  // AD-5: XMLHttpRequest, not fetch -- fetch has no upload-progress event, and FR-007
  // requires per-batch progress. Reused for the non-multipart calls too for one transport idiom.
  private sendXhr(
    method: string,
    url: string,
    body: FormData | string | null,
    isJson: boolean,
    onProgress: ((percent: number) => void) | null,
    onDone: (outcome: XhrOutcome) => void
  ): XMLHttpRequest {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('X-CSRF-Token', this.csrfToken());
    // Without these two, Devise treats the XHR as navigational and 302s instead of a JSON
    // 401/403 (Hiram SOFT-4) -- exemplar api_dashboard_controller.ts:1071,1197.
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('Accept', 'application/json');
    if (isJson) xhr.setRequestHeader('Content-Type', 'application/json');

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      });
    }

    xhr.onload = () => {
      const contentType = xhr.getResponseHeader('Content-Type') || '';
      let json: XhrOutcome['json'] = null;
      if (contentType.indexOf('application/json') !== -1) {
        try {
          json = JSON.parse(xhr.responseText);
        } catch {
          json = null; // A4: unparseable body is treated as a transport failure by classify()
        }
      }
      onDone({ status: xhr.status, json, networkError: false });
    };
    xhr.onerror = () => onDone({ status: 0, json: null, networkError: true });
    xhr.onabort = () => onDone({ status: 0, json: null, networkError: true });

    xhr.send(body);
    return xhr;
  }
}
